import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, RealizationEvItem } from 'types/realization';

interface Options {
  title: string;
  subTitle?: string;
  description: string | React.ReactNode;
  btnText: string;
  hasBackButton?: boolean;
  backBtnText?: string;
  needRule?: boolean;
  needInfo?: boolean;
  onBackButton?: () => void;
  onClose: () => void;
  rule?: string;
  categories?: categories[];
  selectedRealizations?: RealizationEvItem[];
  /** Texto customizado para o checkbox de informações (ELBB). Padrão: texto hardcoded. */
  infoLabel?: string;
  /** Conteúdo customizado para o checkbox do regulamento (ELBB). Padrão: texto hardcoded. */
  ruleLabel?: React.ReactNode;
  /** Classe CSS adicional para o texto dos checkboxes (ELBB). */
  acceptTextClassName?: string;
  /** Exibe o link do regulamento ao lado do checkbox (ELBB). Padrão: true. */
  showRuleLink?: boolean;
  /** Texto do link do regulamento. Padrão: 'IN 403 - 1'. */
  ruleLinkText?: string;
}

const ModalWarning = ({
  title,
  subTitle,
  description,
  categories,
  selectedRealizations,
  btnText,
  hasBackButton,
  backBtnText,
  onBackButton,
  onClose,
  needInfo,
  needRule,
  rule,
  infoLabel,
  ruleLabel,
  acceptTextClassName,
  showRuleLink = true,
  ruleLinkText = 'IN 403 - 1',
}: Options) => {
  const [isCheckedInfo, setIsCheckedInfo] = useState(false);
  const [isCheckedRule, setIsCheckedRule] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // ✅ controle de envio

  const canSubmit =
    (!needInfo || isCheckedInfo) && (!needRule || isCheckedRule) && !isSubmitting;

  return (
    <div className="bg-white rounded-lg">
      <div className="p-4 header-modal">
        <h3 className="text-black-bb text-[26px] md:text-[32px] font-title font-bold">
          {title}
        </h3>
        {subTitle && (
          <p className="mt-4 text-lg text-[#2C323F] font-normal">{subTitle}</p>
        )}
      </div>
      <div className="content px-4 text-lg text-[#2C323F] border-b border-t border-gray-bb-5 font-normal">
        {description}

        {categories && selectedRealizations && (
          <div className="flex flex-col">
            <div className="flex flex-col mt-2 gap-2">
              {categories.map((category) => {
                const selectedRealization = selectedRealizations.filter(
                  (realization) => realization.categoria === category.name,
                );
                return (
                  <div
                    key={category._id}
                    className="flex flex-col justify-center gap-2"
                  >
                    <label
                      htmlFor={category._id}
                      className="text-base font-bold"
                    >
                      {category.name}
                    </label>
                    {selectedRealization.map((item) => (
                      <div
                        key={item._id}
                        className="flex items-center gap-2"
                      >
                        <span className="ml-6 text-base font-normal">
                          - {item.title}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col mt-4 gap-3 accept">
          {needInfo && (
            <label htmlFor="accept_checked" className={`flex items-start gap-3 cursor-pointer ${acceptTextClassName || 'text-sm'}`}>
              <input
                type="checkbox"
                id="accept_checked"
                className="w-5 h-5 min-w-[20px] mt-0.5 cursor-pointer accent-blue-bb-1"
                onChange={(e) => setIsCheckedInfo(e.target.checked)}
              />
              <span>{infoLabel || 'Li e estou de acordo com as informações disponibilizadas acima.'}</span>
            </label>
          )}

          {needRule && (
            <div className={`flex flex-row gap-2 mt-2 ${acceptTextClassName || 'text-sm'}`}>
              <label htmlFor="accept_rule" className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="accept_rule"
                  className="w-5 h-5 min-w-[20px] mt-0.5 cursor-pointer accent-blue-bb-1"
                  onChange={(e) => {
                    setIsCheckedRule(e.target.checked);
                  }}
                />
                {ruleLabel || (
                  <span>
                    Li e estou de acordo com o regulamento do Prêmio BB disponível na{' '}
                    {showRuleLink && (
                      <Link
                        to={rule?.toString() || ''}
                        target="_blank"
                        className="underline text-blue-bb-1 hover:no-underline font-semibold"
                      >
                        {ruleLinkText}
                      </Link>
                    )}
                  </span>
                )}
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-end p-4 actions">
        {hasBackButton && (
          <button
            className="bg-[#E0E9FF] rounded-[4px] mr-2 text-sm font-title uppercase text-blue-bb-1 py-3 px-4 font-bold"
            onClick={onBackButton}
          >
            {!!backBtnText ? backBtnText : 'Voltar'}
          </button>
        )}

        <button
          onClick={() => {
            if (!canSubmit) return;
            setIsSubmitting(true); // ✅ trava múltiplos envios
            onClose();
          }}
          disabled={!canSubmit}
          className={`rounded-[4px] disabled:cursor-not-allowed text-sm font-title uppercase py-3 px-4 font-bold ${
            canSubmit ? 'bg-yellow-bb text-blue-bb-1' : 'text-gray-bb-2 opacity-70'
          }`}
        >
          {btnText}
        </button>
      </div>
    </div>
  );
};

export default ModalWarning;
